import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  KeyRound,
  Plus,
  Trash2,
  Loader2,
  Shield,
  RefreshCw,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import {
  usePaginatedData,
  PaginatedResponse,
} from "@/hooks/usePaginatedData";
import { workflowsApi, SecretName } from "@/services/api/workflows";

export const Route = createFileRoute("/portal/platform/secrets")({
  component: IntegrationSecretsPage,
});

// Same slug rule the backend enforces — surface it early so the dialog can
// validate before the round-trip.
const NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function IntegrationSecretsPage() {
  const { token } = useAuth();
  const { selectedOrganisation, loading: orgLoading } = usePortalData();
  const { isOwner } = usePermissions();
  const selectedOrgId = selectedOrganisation?.organisationId || "";

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "created_at">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Create/rotate dialog
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [toDelete, setToDelete] = useState<SecretName | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchFn = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<SecretName>> => {
      if (!token || !selectedOrgId || !isOwner) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      try {
        const response = await workflowsApi.listSecrets(
          {
            page,
            page_size: pageSize,
            search: searchTerm || undefined,
            sort: sortBy,
            order: sortOrder,
          },
          token,
        );
        return {
          data: response.items || [],
          totalCount: response.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load secrets:", error);
        toast.error("Failed to load secrets");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, selectedOrgId, isOwner, searchTerm, sortBy, sortOrder],
  );

  const {
    data: secrets,
    isLoading,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<SecretName>({
    fetchFn,
    initialPageSize: 25,
    dependencies: [token, selectedOrgId, isOwner, searchTerm, sortBy, sortOrder],
  });

  function handleSort(column: "name" | "created_at") {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  const getSortDirection = (column: string) =>
    sortBy === column ? sortOrder : null;

  const resetCreateForm = () => {
    setNewName("");
    setNewValue("");
    setNewDescription("");
  };

  const handleSave = async () => {
    if (!token) return;
    const name = newName.trim();
    if (!NAME_RE.test(name)) {
      toast.error(
        "Name must be lowercase, start with a letter (e.g. stripe_key)",
      );
      return;
    }
    if (!newValue) {
      toast.error("Enter a value");
      return;
    }
    setSaving(true);
    try {
      await workflowsApi.upsertSecret(
        { name, value: newValue, description: newDescription || null },
        token,
      );
      toast.success(`Secret "${name}" saved`);
      setShowCreate(false);
      resetCreateForm();
      refresh();
    } catch (error) {
      console.error("Failed to save secret:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save secret",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !toDelete) return;
    setDeleting(true);
    try {
      await workflowsApi.deleteSecret(toDelete.name, token);
      toast.success(`Secret "${toDelete.name}" deleted`);
      setToDelete(null);
      refresh();
    } catch (error) {
      console.error("Failed to delete secret:", error);
      toast.error("Failed to delete secret");
    } finally {
      setDeleting(false);
    }
  };

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
        <Shield className="mx-auto h-12 w-12 text-slate-300" />
        <p className="mt-4 text-slate-600">
          Integration secrets can only be managed by the organisation owner.
        </p>
      </div>
    );
  }

  if (!selectedOrgId) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
        <KeyRound className="mx-auto h-12 w-12 text-slate-300" />
        <p className="mt-4 text-slate-600">
          Select an organisation from the header to manage secrets.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 flex items-center gap-3">
            <ArrowUpRight className="h-8 w-8" />
            Integration secrets
          </h1>
          <p className="mt-2 text-lg text-slate-600">
            <span className="font-medium">Outbound</span> — encrypted
            credentials (API tokens, keys) your workflows use to call external
            services. Referenced in a workflow as{" "}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono">
              ${"{org.secrets.<name>}"}
            </code>
            . Values are write-only and never shown again.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="rounded-full">
          <Plus className="h-4 w-4 mr-2" />
          New secret
        </Button>
      </div>

      {/* Content panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search secrets…"
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={refresh}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Table */}
        {isLoading && secrets.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : secrets.length === 0 ? (
          <div className="text-center py-12">
            <KeyRound className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No secrets match your search" : "No secrets yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term."
                : "Add a credential your workflows can use to call an external API."}
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add your first secret
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => handleSort("name")}
                  >
                    Name
                  </SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>
                    Description
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("created_at")}
                    onSort={() => handleSort("created_at")}
                  >
                    Created
                  </SortableTableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className="w-[50px]"
                  >
                    {" "}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {secrets.map((secret) => (
                  <TableRow key={secret.name}>
                    <TableCell className="font-mono font-medium">
                      {secret.name}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {secret.description || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(secret.created_at_utc)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => setToDelete(secret)}
                        aria-label={`Delete ${secret.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={[10, 25, 50, 100]}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / rotate dialog */}
      <Dialog
        open={showCreate}
        onOpenChange={(open) => {
          setShowCreate(open);
          if (!open) resetCreateForm();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add or rotate a secret</DialogTitle>
            <DialogDescription>
              Stored encrypted and referenced by name. Using an existing name
              rotates its value. The value is never shown again after saving.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="secret-name">Name</Label>
              <Input
                id="secret-name"
                placeholder="stripe_key"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Lowercase, starts with a letter. Referenced as{" "}
                <code className="font-mono">
                  ${"{org.secrets."}
                  {newName.trim() || "<name>"}
                  {"}"}
                </code>
                .
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="secret-value">Value</Label>
              <Input
                id="secret-value"
                type="password"
                placeholder="sk_live_…"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                autoComplete="new-password"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secret-description">Description (optional)</Label>
              <Input
                id="secret-description"
                placeholder="Stripe production key"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save secret"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete secret</AlertDialogTitle>
            <AlertDialogDescription>
              Delete the secret &quot;{toDelete?.name}&quot;? Any workflow that
              references{" "}
              <code className="font-mono">
                ${"{org.secrets."}
                {toDelete?.name}
                {"}"}
              </code>{" "}
              will fail until it&apos;s replaced. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete secret"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
